import { useState } from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import MenuIcon from '@mui/icons-material/Menu';
import { Dashboard, Gavel, History, Settings, Article } from '@mui/icons-material';
import { routes } from './routes';
import { Link as RouterLink, Outlet, useLocation } from 'react-router';
import Logo02 from './assets/02-logo-menu-cuadrado.svg';
import { useTranslation } from 'react-i18next';

const DRAWER_WIDTH = 220;
const APPBAR_HEIGHT = 52;

interface DrawerContentProps {
    currentPath: string;
    onNavigate?: () => void;
}

function DrawerContent({ currentPath, onNavigate }: DrawerContentProps) {
    const { t } = useTranslation();

    const NAV_ITEMS = [
        { label: t('nav.dashboard'), path: routes.dashboard.path, icon: <Dashboard sx={{ fontSize: 20 }} /> },
        { label: t('nav.rules'), path: routes.rules.path, icon: <Gavel sx={{ fontSize: 20 }} /> },
        { label: t('nav.history'), path: routes.history.path, icon: <History sx={{ fontSize: 20 }} /> },
        { label: t('nav.settings'), path: routes.settings.path, icon: <Settings sx={{ fontSize: 20 }} /> },
        { label: t('nav.logs'), path: routes.logs.path, icon: <Article sx={{ fontSize: 20 }} /> },
    ];

    return (
        <Box sx={{ bgcolor: '#020617', height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Brand */}
            <Box
                component={RouterLink}
                to={routes.home.path}
                onClick={onNavigate}
                sx={{ px: 2.5, height: APPBAR_HEIGHT, display: 'flex', alignItems: 'center', gap: 1.5, textDecoration: 'none', flexShrink: 0, borderBottom: '1px solid rgba(255,255,255,0.06)', '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' } }}
            >
                <Box component="img" src={Logo02} alt="FilesWatcher" sx={{ height: 28, width: 28, borderRadius: 1 }} />
                <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', lineHeight: 1 }}>
                    <Box component="span" sx={{ color: 'white' }}>Files</Box>
                    <Box component="span" sx={{ color: '#38BDF8' }}>Watcher</Box>
                </Typography>
            </Box>

            {/* Nav items */}
            <Box sx={{ flex: 1, py: 1.5, px: 1 }}>
                {NAV_ITEMS.map((item) => {
                    const active = item.path === routes.dashboard.path
                        ? currentPath === item.path
                        : currentPath.startsWith(item.path);
                    return (
                        <Box
                            key={item.path}
                            component={RouterLink}
                            to={item.path}
                            onClick={onNavigate}
                            sx={{
                                display: 'flex', alignItems: 'center', gap: 1.5,
                                px: 1.5, py: 1, mb: 0.25, borderRadius: 1.5,
                                color: active ? 'white' : 'rgba(255,255,255,0.55)',
                                bgcolor: active ? 'rgba(37,99,235,0.35)' : 'transparent',
                                textDecoration: 'none',
                                transition: 'background-color 0.15s, color 0.15s',
                                '&:hover': {
                                    bgcolor: active ? 'rgba(37,99,235,0.35)' : 'rgba(255,255,255,0.07)',
                                    color: 'rgba(255,255,255,0.9)',
                                },
                            }}
                        >
                            <Box sx={{ color: active ? '#60A5FA' : 'rgba(255,255,255,0.4)', display: 'flex', flexShrink: 0 }}>
                                {item.icon}
                            </Box>
                            <Typography sx={{ fontSize: '0.875rem', fontWeight: active ? 600 : 400 }}>
                                {item.label}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}

function Layout() {
    const [mobileOpen, setMobileOpen] = useState(false);
    const location = useLocation();
    const { t, i18n } = useTranslation();

    const NAV_ITEMS_FOR_TITLE = [
        { label: t('nav.dashboard'), path: routes.dashboard.path },
        { label: t('nav.rules'), path: routes.rules.path },
        { label: t('nav.history'), path: routes.history.path },
        { label: t('nav.settings'), path: routes.settings.path },
        { label: t('nav.logs'), path: routes.logs.path },
    ];

    function getPageTitle(pathname: string): string {
        if (pathname.includes('/edit')) return t('nav.editRule');
        if (pathname === routes.newRule.path) return t('nav.newRule');
        return NAV_ITEMS_FOR_TITLE.find(item => pathname.startsWith(item.path))?.label ?? '';
    }

    const pageTitle = getPageTitle(location.pathname);

    const handleLanguageChange = (lng: string) => {
        i18n.changeLanguage(lng);
        localStorage.setItem('i18nextLng', lng);
    };

    return (
        <Box sx={{ display: 'flex' }}>
            {/* AppBar — ocupa solo el ancho del contenido en desktop */}
            <AppBar
                position="fixed"
                sx={{
                    bgcolor: '#020617',
                    boxShadow: 'none',
                    borderBottom: '1px solid rgba(255,255,255,0.06)',
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
                    ml: { md: `${DRAWER_WIDTH}px` },
                }}
            >
                <Toolbar sx={{ minHeight: `${APPBAR_HEIGHT}px !important`, px: { xs: 2, md: 3 } }}>
                    <IconButton color="inherit" edge="start" onClick={() => setMobileOpen(true)} sx={{ mr: 1.5, display: { md: 'none' } }}>
                        <MenuIcon />
                    </IconButton>
                    <Typography variant="body1" fontWeight={600} sx={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.95rem', flexGrow: 1 }}>
                        {pageTitle}
                    </Typography>
                    {/* Language switcher */}
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {(['es', 'en'] as const).map((lng) => (
                            <Box
                                key={lng}
                                component="button"
                                onClick={() => handleLanguageChange(lng)}
                                sx={{
                                    bgcolor: i18n.language === lng ? 'rgba(37,99,235,0.7)' : 'transparent',
                                    color: i18n.language === lng ? 'white' : 'rgba(255,255,255,0.45)',
                                    border: '1px solid',
                                    borderColor: i18n.language === lng ? 'rgba(37,99,235,0.9)' : 'rgba(255,255,255,0.12)',
                                    borderRadius: 1,
                                    px: 1,
                                    py: 0.25,
                                    fontSize: '0.72rem',
                                    fontWeight: 700,
                                    letterSpacing: '0.04em',
                                    textTransform: 'uppercase',
                                    cursor: 'pointer',
                                    transition: 'all 0.15s',
                                    lineHeight: 1.6,
                                    '&:hover': {
                                        color: 'white',
                                        borderColor: 'rgba(255,255,255,0.35)',
                                    },
                                }}
                            >
                                {lng}
                            </Box>
                        ))}
                    </Box>
                </Toolbar>
            </AppBar>

            {/* Sidebar permanente — desktop */}
            <Drawer
                variant="permanent"
                sx={{
                    display: { xs: 'none', md: 'block' },
                    width: DRAWER_WIDTH,
                    flexShrink: 0,
                    '& .MuiDrawer-paper': {
                        width: DRAWER_WIDTH,
                        boxSizing: 'border-box',
                        bgcolor: '#020617',
                        border: 'none',
                        borderRight: '1px solid rgba(255,255,255,0.06)',
                    },
                }}
                open
            >
                <DrawerContent currentPath={location.pathname} />
            </Drawer>

            {/* Sidebar temporal — móvil */}
            <Drawer
                variant="temporary"
                open={mobileOpen}
                onClose={() => setMobileOpen(false)}
                ModalProps={{ keepMounted: true }}
                sx={{
                    display: { xs: 'block', md: 'none' },
                    '& .MuiDrawer-paper': { width: DRAWER_WIDTH, bgcolor: '#020617' },
                }}
            >
                <DrawerContent currentPath={location.pathname} onNavigate={() => setMobileOpen(false)} />
            </Drawer>

            {/* Contenido principal */}
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
                    height: '100vh',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <Box sx={{ height: APPBAR_HEIGHT, flexShrink: 0 }} />
                <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                    <Outlet />
                </Box>
            </Box>
        </Box>
    );
}

export default Layout;
